import { useState, useEffect, useCallback } from 'react'
import { Star, Folder, FolderPlus } from 'lucide-react'
import { bookmarkApi, type BookmarkFolder } from '../api/bookmarks'
import Popover from './Popover'

interface BookmarkStarProps {
  pmid: string
  className?: string
  showLabel?: boolean
}

export default function BookmarkStar({ pmid, className = '', showLabel = false }: BookmarkStarProps) {
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [folders, setFolders] = useState<BookmarkFolder[]>([])
  const [newFolderMode, setNewFolderMode] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')

  // Check bookmark status on mount
  useEffect(() => {
    const check = async () => {
      try {
        const bookmarked = await bookmarkApi.checkBookmark(pmid)
        setIsBookmarked(bookmarked)
      } catch {
        // ignore
      }
    }
    check()
  }, [pmid])

  // Load folders when popover opens
  useEffect(() => {
    if (!popoverOpen) return
    const load = async () => {
      try {
        const folderList = await bookmarkApi.listFolders()
        setFolders(folderList)
      } catch (err) {
        console.error('Failed to load folders:', err)
      }
    }
    load()
  }, [popoverOpen])

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (isBookmarked) {
      // Remove bookmark
      setIsLoading(true)
      bookmarkApi.removeBookmark(pmid)
        .then(() => setIsBookmarked(false))
        .catch((err: any) => alert(err?.response?.data?.detail || 'Failed to remove bookmark'))
        .finally(() => setIsLoading(false))
    } else {
      // Open folder picker
      setPopoverAnchor(e.currentTarget)
      setPopoverOpen(true)
      setNewFolderMode(false)
      setNewFolderName('')
    }
  }, [isBookmarked, pmid])

  const addBookmark = async (folderId: string | null) => {
    setIsLoading(true)
    try {
      await bookmarkApi.addBookmark(pmid, folderId)
      setIsBookmarked(true)
      setPopoverOpen(false)
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to add bookmark')
    } finally {
      setIsLoading(false)
    }
  }

  const createFolderAndBookmark = async () => {
    const name = newFolderName.trim()
    if (!name) return
    try {
      const folder = await bookmarkApi.createFolder({ name })
      setFolders(prev => [...prev, folder])
      setNewFolderMode(false)
      setNewFolderName('')
      await addBookmark(folder.folder_id)
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to create folder')
    }
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`inline-flex items-center gap-1 transition-colors ${
          isBookmarked
            ? 'text-yellow-500 hover:text-yellow-600'
            : 'text-gray-300 hover:text-yellow-400'
        } ${isLoading ? 'opacity-50' : ''} ${className}`}
        title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
      >
        <Star
          className="w-3.5 h-3.5"
          fill={isBookmarked ? 'currentColor' : 'none'}
        />
        {showLabel && (
          <span className="text-xs">{isBookmarked ? 'Bookmarked' : 'Bookmark'}</span>
        )}
      </button>

      <Popover
        anchor={popoverAnchor}
        open={popoverOpen}
        onClose={() => setPopoverOpen(false)}
        align="left"
        width={240}
      >
        <div className="text-sm">
          <div className="text-xs text-gray-500 px-2 pt-1 pb-2">Save to folder</div>
          <div className="max-h-56 overflow-y-auto">
            {folders.map(f => (
              <button
                key={f.folder_id}
                onClick={() => addBookmark(f.folder_id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
              >
                <Folder className="w-3.5 h-3.5 text-gray-500" />
                <span className="flex-1 truncate">{f.name}</span>
                {typeof f.bookmark_count === 'number' && (
                  <span className="text-[10px] text-gray-400">{f.bookmark_count}</span>
                )}
              </button>
            ))}
            <button
              onClick={() => addBookmark(null)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
            >
              <Folder className="w-3.5 h-3.5 text-gray-300" />
              <span className="flex-1 truncate italic text-gray-600">Unsorted</span>
            </button>
          </div>
          <div className="border-t mt-1 pt-1">
            {newFolderMode ? (
              <div className="px-2 py-1.5 flex gap-1">
                <input
                  autoFocus
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') createFolderAndBookmark()
                    else if (e.key === 'Escape') { setNewFolderMode(false); setNewFolderName('') }
                  }}
                  placeholder="Folder name…"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                />
                <button
                  onClick={createFolderAndBookmark}
                  disabled={!newFolderName.trim()}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                onClick={() => setNewFolderMode(true)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left text-blue-700"
              >
                <FolderPlus className="w-3.5 h-3.5" />
                <span>New folder…</span>
              </button>
            )}
          </div>
        </div>
      </Popover>
    </>
  )
}
